import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  OrganizationsListResponseSchema,
  OrganizationSchema,
  type Organization,
  type OrganizationListItem,
} from '@repo/api-client/dtos';

import { useQuery } from '@tanstack/react-query';
import { getJSON } from '../internal/http';

type QueryEnableOptions = { enabled?: boolean };

/**
 * List the orgs the current user belongs to.
 *
 * Returns the wire-format list items (including the caller's role on
 * each org). Pagination is left out for v0.1 because the realistic
 * cap is a few dozen orgs per user; if that changes we can switch to
 * `useInfiniteQuery` without changing the public surface.
 */
export function useOrganizations(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
    refetch,
  } = useQuery({
    queryKey: queryKeys.organizations.all(),
    queryFn: () => getJSON(http, '/orgs', OrganizationsListResponseSchema),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    organizations: data?.items as OrganizationListItem[] | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Read a full org (including `nextInvoiceSeq` and the optional tax /
 * registration metadata that's only on the detail endpoint).
 */
export function useOrganization(
  overrideOrgId?: string,
  options: QueryEnableOptions = {},
) {
  const http = useHttpClient();
  const activeOrgId = useActiveOrganizationId();
  const organizationId = overrideOrgId || activeOrgId;
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data: organization,
    refetch,
  } = useQuery({
    queryKey: organizationId
      ? queryKeys.organizations.detail(organizationId)
      : ['organizations', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${organizationId}`, OrganizationSchema),
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  return { organization: organization as Organization | undefined, loading, error, refetch };
}
